import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/base/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import {
  siteInfo,
  heroSlides,
  serviceCategories,
  featuredEvents,
  articles,
  faqItems,
  stats,
  partners,
  trainingPrograms,
  featuredProducts,
} from '@/mocks/data';

function HomeSEO() {
  return (
    <SEO
      title={`${siteInfo.name} - ${siteInfo.tagline} | ${siteInfo.keywords.split(',')[0]}`}
      description={siteInfo.description}
      keywords={siteInfo.keywords}
      canonicalUrl={siteInfo.url}
      ogImage={heroSlides[0].image}
      structuredData={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            name: siteInfo.name,
            url: siteInfo.url,
            description: siteInfo.description,
            logo: `${siteInfo.url}/logo.png`,
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: siteInfo.phone,
              contactType: 'customer service',
              availableLanguage: 'Chinese',
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: siteInfo.address,
              addressCountry: 'CN',
            },
            sameAs: [
              'https://weibo.com/manguansports',
              'https://www.douyin.com/user/manguansports',
            ],
          },
          {
            '@type': 'WebSite',
            name: siteInfo.name,
            url: siteInfo.url,
            description: siteInfo.description,
            inLanguage: 'zh-CN',
          },
          {
            '@type': 'WebPage',
            name: `${siteInfo.name} - 首页`,
            url: siteInfo.url,
            description: siteInfo.description,
            isPartOf: { '@id': `${siteInfo.url}#website` },
          },
          {
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          },
        ],
      }}
    />
  );
}

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <Layout>
      <HomeSEO />

      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        {heroSlides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-700 ${
              idx === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-center">
          <div className="container-custom w-full">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-4 md:mb-6 leading-tight font-heading">
                {heroSlides[currentSlide].title}
              </h1>
              <p className="text-sm md:text-lg text-background-100/90 mb-6 md:mb-8 leading-relaxed">
                {heroSlides[currentSlide].subtitle}
              </p>
              <Link
                to={heroSlides[currentSlide].link}
                className="inline-flex items-center gap-2 px-6 py-3 md:px-8 md:py-3.5 bg-primary-500 text-background-50 rounded-lg text-sm md:text-base font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                {heroSlides[currentSlide].cta}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentSlide ? 'bg-primary-500 w-7' : 'bg-background-50/60 hover:bg-background-50/80'
              }`}
              aria-label={`第${idx + 1}张`}
            />
          ))}
        </div>
      </section>

      <section className="py-10 md:py-14 bg-background-50">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center p-4 md:p-6 bg-background-100 rounded-lg">
                <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 bg-primary-100 rounded-lg flex items-center justify-center">
                  <i className={`${stat.icon} text-lg md:text-xl text-primary-500`}></i>
                </div>
                <div className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{stat.value}</div>
                <div className="text-xs md:text-sm text-foreground-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-100">
        <div className="container-custom">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              核心服务
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              覆盖体育赛事、运动培训和体育用品三大领域，为您提供全方位的专业体育服务
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {serviceCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/services?tab=${cat.id}`}
                className="group bg-background-50 rounded-lg overflow-hidden hover:ring-2 hover:ring-primary-300 transition-all duration-300 no-underline"
              >
                <div className="relative h-48 md:h-56 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                      <i className={`${cat.icon} text-sm text-background-50`}></i>
                    </div>
                    <span className="text-base md:text-lg font-bold text-background-50 font-heading">
                      {cat.name}
                    </span>
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-3">
                    {cat.description}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.features.map((f) => (
                      <span key={f} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-md text-xs">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-50">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                精选赛事
              </h2>
              <p className="text-sm text-foreground-600">即将举办的热门体育赛事</p>
            </div>
            <Link
              to="/services?tab=events"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {featuredEvents.map((event) => (
              <div key={event.id} className="bg-background-100 rounded-lg overflow-hidden group cursor-pointer">
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      event.status === '报名中' ? 'bg-accent-500 text-background-50' :
                      event.status === '进行中' ? 'bg-secondary-500 text-background-50' :
                      'bg-foreground-700 text-background-100'
                    }`}>
                      {event.status}
                    </span>
                  </div>
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-2 line-clamp-2 font-heading">
                    {event.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {event.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-map-pin-line"></i> {event.location}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-foreground-500">
                    <i className="ri-user-line mr-1"></i> {event.participants.toLocaleString()} 人参与
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-100">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                热门课程
              </h2>
              <p className="text-sm text-foreground-600">精选专业运动培训课程</p>
            </div>
            <Link
              to="/services?tab=training"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {trainingPrograms.map((p) => (
              <div key={p.id} className="bg-background-50 rounded-lg overflow-hidden group cursor-pointer">
                <div className="relative h-40 md:h-44 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-0.5 bg-primary-500 text-background-50 rounded-md text-xs font-medium">
                      {p.level}
                    </span>
                  </div>
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-1 font-heading">{p.name}</h3>
                  <p className="text-xs text-foreground-600 mb-2">{p.coach}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-primary-500">{p.price}</span>
                    <span className="text-xs text-foreground-500">{p.duration} | {p.age}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-50">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                热门商品
              </h2>
              <p className="text-sm text-foreground-600">严选优质体育用品</p>
            </div>
            <Link
              to="/services?tab=products"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {featuredProducts.map((p) => (
              <div key={p.id} className="bg-background-100 rounded-lg overflow-hidden group cursor-pointer" data-product-shop>
                <div className="relative h-36 md:h-40 overflow-hidden bg-background-200">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-3">
                  <p className="text-xs text-foreground-500 mb-0.5">{p.brand}</p>
                  <h3 className="text-sm font-bold text-foreground-900 mb-1 line-clamp-1">{p.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-primary-500">{p.price}</span>
                    <span className="text-xs text-foreground-500 flex items-center gap-0.5">
                      <i className="ri-star-fill text-accent-500 text-xs"></i> {p.rating}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-100">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                体育资讯
              </h2>
              <p className="text-sm text-foreground-600">最新体育动态与专业知识</p>
            </div>
            <Link
              to="/articles"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {articles.slice(0, 3).map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="bg-background-50 rounded-lg overflow-hidden group no-underline"
              >
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 bg-accent-500 text-background-50 rounded-md text-xs font-medium">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors font-heading">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i> {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i> {article.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-50">
        <div className="container-custom">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              合作伙伴
            </h2>
            <p className="text-sm text-foreground-600">携手行业领军机构，共创体育未来</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {partners.map((p) => (
              <div
                key={p}
                className="bg-background-100 rounded-lg py-3 px-2 text-center text-xs md:text-sm text-foreground-700 font-medium hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-default"
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-100">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-600">您可能关心的问题，这里都有答案</p>
          </div>
          <div className="space-y-3">
            {faqItems.map((faq, idx) => (
              <details key={idx} className="group bg-background-50 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                  <span className="text-sm md:text-base font-medium text-foreground-900 font-heading pr-4">
                    {faq.question}
                  </span>
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-200 flex items-center justify-center group-open:rotate-45 transition-transform duration-200">
                    <i className="ri-add-line text-sm text-foreground-600"></i>
                  </span>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-primary-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_50%)]" />
        <div className="container-custom relative z-10 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-3 font-heading">
            准备好开启您的冠军之路了吗？
          </h2>
          <p className="text-sm md:text-base text-background-50/80 mb-6 md:mb-8 max-w-xl mx-auto">
            无论是赛事组织、专业培训还是装备选购，满冠体育都将竭诚为您服务
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/about"
              className="px-6 py-3 bg-background-50 text-primary-600 rounded-lg text-sm md:text-base font-medium hover:bg-background-100 transition-all duration-200 whitespace-nowrap"
            >
              立即咨询
            </Link>
            <Link
              to="/services"
              className="px-6 py-3 border-2 border-background-50/40 text-background-50 rounded-lg text-sm md:text-base font-medium hover:bg-background-50/10 transition-all duration-200 whitespace-nowrap"
            >
              探索服务
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}